import React, { useState, useEffect } from 'react';
import {
  isLocalDevelopment,
  getDevUsers,
  getSelectedDevUser,
  switchDevUser,
  subscribeToDevUserChange,
  isAnonymousDevUser,
  ANONYMOUS_USER_ID,
  DevUser
} from '../services/tokenService';

/**
 * Development-only floating toolbar for switching between test users.
 * Only renders in local development environment (localhost/127.0.0.1).
 * Session-based - resets to default user on page refresh.
 */
const DevUserToolbar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [selectedUser, setSelectedUser] = useState<DevUser>(getSelectedDevUser());
  const [isAnonymous, setIsAnonymous] = useState(isAnonymousDevUser());
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in local development
    setIsVisible(isLocalDevelopment());

    // Subscribe to user changes
    const unsubscribe = subscribeToDevUserChange((user) => {
      setSelectedUser(user);
      setIsAnonymous(isAnonymousDevUser());
    });

    return unsubscribe;
  }, []);

  if (!isVisible) {
    return null;
  }

  const users = getDevUsers();

  const handleUserChange = (userId: string) => {
    switchDevUser(userId);
    // Reload the page to refresh all data with new user context
    window.location.reload();
  };

  return (
    <div
      className="fixed bottom-2 right-2 sm:bottom-4 sm:right-4 z-50 font-mono text-sm"
      data-testid="dev-user-toolbar"
    >
      {isCollapsed ? (
        // Collapsed state — small icon-only pill on mobile, full badge on sm+
        <button
          onClick={() => setIsCollapsed(false)}
          className={`flex items-center gap-2 rounded-full sm:rounded-lg shadow-lg transition-colors p-2 sm:px-3 sm:py-2 ${
            isAnonymous
              ? 'bg-gray-500 text-gray-100 hover:bg-gray-400'
              : 'bg-amber-500 text-amber-950 hover:bg-amber-400'
          }`}
          title={isAnonymous ? 'Anonymous (tap to expand)' : `${selectedUser.displayName} (tap to expand)`}
          aria-label={isAnonymous ? 'Anonymous, tap to open dev toolbar' : `${selectedUser.displayName}, tap to open dev toolbar`}
          data-testid="dev-toolbar-expand"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <span className="hidden sm:inline font-semibold">{isAnonymous ? 'Anonymous' : selectedUser.displayName}</span>
        </button>
      ) : (
        // Expanded state - full panel
        <div className="bg-amber-100 dark:bg-amber-900 border-2 border-amber-500 rounded-lg shadow-xl p-4 min-w-[250px]">
          <div className="flex items-center justify-between mb-3">
            <span className="font-bold text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              DEV MODE
            </span>
            <button
              onClick={() => setIsCollapsed(true)}
              className="text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100"
              title="Collapse"
              data-testid="dev-toolbar-collapse"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="mb-2">
            <label
              htmlFor="dev-user-select"
              className="block text-xs text-amber-700 dark:text-amber-300 mb-1"
            >
              Switch User:
            </label>
            <select
              id="dev-user-select"
              value={isAnonymous ? ANONYMOUS_USER_ID : selectedUser.id}
              onChange={(e) => handleUserChange(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-amber-400 dark:border-amber-600 rounded text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:outline-none"
              data-testid="dev-user-select"
            >
              <option value={ANONYMOUS_USER_ID}>
                🚫 Anonymous (Not logged in)
              </option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName} ({user.id})
                </option>
              ))}
            </select>
          </div>

          <div className="text-xs text-amber-700 dark:text-amber-300 mt-3 pt-2 border-t border-amber-300 dark:border-amber-700">
            {isAnonymous ? (
              <p className="italic">No user selected — unauthenticated mode</p>
            ) : (
              <>
                <p className="mb-1">
                  <strong>ID:</strong> {selectedUser.id}
                </p>
                <p>
                  <strong>Email:</strong> {selectedUser.email}
                </p>
              </>
            )}
          </div>

          <div className="text-xs text-amber-600 dark:text-amber-400 mt-2 italic">
            Page will reload on user change
          </div>
        </div>
      )}
    </div>
  );
};

export default DevUserToolbar;
